<script setup lang="ts">
import { KitColour, type KitResponse, KitType } from "mws-catalog-ts-sdk";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

import CardVue from "@/common/components/card/CardVue.vue";
import FormSection from "@/common/components/FormSection/FormSection.vue";
import { useUpdateCatalogKitMutation } from "@/kits/api/queries";

definePage({
  alias: ["/teams/:teamId?/kits/:kitId/manufacturer"],
  meta: {
    auth: ["Kits.Read"],
  },
});

const { kit } = defineProps<{
  kit: KitResponse;
}>();

const { t } = useI18n();
const toast = useToast();
const { mutateAsync: updateKit } = useUpdateCatalogKitMutation();

const initialFormData = computed(() => ({
  nsPid: kit.nsPid,
  sPid: kit.sPid,
}));

const onSubmit = async (form: Pick<KitResponse, "nsPid" | "sPid">) => {
  // The update replaces every kit detail, so the rest is sent as it is.
  await updateKit({
    kitId: kit.id,
    request: {
      ...kit,
      type: kit.type ?? KitType.Home,
      colour: kit.colour ?? KitColour.White,
      nsPid: form.nsPid,
      sPid: form.sPid,
    },
  });
  toast.success(t("kits.manufacturer.updated"));
};
</script>

<template>
  <div>
    <CardVue>
      <FormKitForm
        :value="initialFormData"
        submit-label="Submit"
        :submit-attrs="{ 'data-test': 'kit-manufacturer-submit' }"
        @submit="onSubmit"
      >
        <FormSection
          :uses-column-layout="true"
          :title="t('kits.manufacturer.importIdentifiers.title')"
          :description="t('kits.manufacturer.importIdentifiers.description')"
          :display-divider="false"
        >
          <FormKit
            type="text"
            name="nsPid"
            :label="t('kits.manufacturer.importIdentifiers.nsPidLabel')"
            data-test="kit-ns-pid"
          />
          <FormKit
            type="text"
            name="sPid"
            :label="t('kits.manufacturer.importIdentifiers.sPidLabel')"
            data-test="kit-s-pid"
          />
        </FormSection>
      </FormKitForm>
    </CardVue>
  </div>
</template>
